import { PUBLIC_STATS } from "@kolony/shared";
import { svgEl } from "./kit";
import type { ActivityStat } from "../api/stats";

// The landing page's lower half (D221, `design_handoff_main_menu` §4–§5): the proof bar and
// the testimonial band.
//
// Both sections are about EVIDENCE, which makes them the two places on the whole product
// where it is easiest to lie. The handoff says so itself — "numbers come from `daily_stats`;
// a stat renders only above its floor — never show zeros", and of the testimonials, "all
// quotes/ratings in the prototype are sample copy — replace with real post-game ratings and
// consented player quotes before shipping".
//
// So this module renders NOTHING it cannot source. A stat the server withheld is absent
// rather than zero, and the quote row is empty until real, consented quotes exist — which
// today they do not, because the post-game rating prompt that would collect them is the
// separate feature the handoff scopes out. What survives is the section's own closing CTA,
// which needs no evidence to be true.

/** One cell of the proof bar. */
export interface ProofStat {
  value: string;
  label: string;
}

/** A real, consented player quote. There is no source of these yet — see the module note. */
export interface Testimonial {
  name: string;
  /** Avatar letter. Supplied rather than derived, so a display name is never sliced blind. */
  initial: string;
  /** The player's own seat colour, per the handoff. */
  color: string;
  /** "212 games", "Rank: Duke" — a public fact about them, never a claim about the game. */
  meta: string;
  stars: number;
  text: string;
}

/** The aggregate line under the quotes. Rendered only when there are real ratings behind it. */
export interface RatingAggregate {
  average: number;
  count: number;
}

const group = (n: number): string => n.toLocaleString("en-US");

/**
 * The proof bar's cells, from whatever the server was willing to disclose.
 *
 * Pure, because this is the honesty rule itself and it should be a unit test rather than
 * something you confirm by staring at a quiet server. An absent field is a field the server
 * floored: it renders as no cell at all, never as "0 games this week" — the one thing a
 * front door must never say about itself.
 */
export function proofStats(activity: ActivityStat | null): ProofStat[] {
  const cells: ProofStat[] = [];
  if (activity?.games !== undefined)
    cells.push({ value: group(activity.games), label: "games this week" });
  if (activity?.online !== undefined)
    cells.push({ value: group(activity.online), label: "settlers online now" });
  // Not a measurement and not dressed as one: a claim about the product that the strap line
  // already makes, and that anyone can check by pressing the button above it.
  cells.push({ value: "10 s", label: "link to first roll" });
  return cells;
}

/** True when the aggregate is worth showing — the same floor logic, applied client-side. */
export function showsAggregate(agg: RatingAggregate | null): boolean {
  return agg !== null && agg.count >= PUBLIC_STATS.floor;
}

function star(doc: Document, px: number): SVGSVGElement {
  const s = svgEl<SVGSVGElement>(doc, "svg", {
    width: px,
    height: px,
    viewBox: "0 0 16 16",
  });
  s.append(
    svgEl(doc, "path", {
      d: "M8 1 L10 5.8 L15 6.2 L11.2 9.5 L12.4 14.5 L8 11.8 L3.6 14.5 L4.8 9.5 L1 6.2 L6 5.8 Z",
      fill: "var(--hud-gold, #e8b64c)",
    }),
  );
  return s;
}

export interface ProofBar {
  readonly root: HTMLElement;
  update(activity: ActivityStat | null): void;
}

/**
 * The proof band. It mounts EMPTY and fills in when the fetch lands, so a slow or failed
 * request leaves a quiet band rather than a row of dashes pretending to be data.
 */
export function createProofBar(
  doc: Document,
  opts: { reducedMotion?: () => boolean } = {},
): ProofBar {
  const root = doc.createElement("section");
  root.className = "proof-bar";
  root.dataset["proofBar"] = "1";
  const row = doc.createElement("div");
  row.className = "proof-row";
  root.append(row);

  /**
   * D237: motion is unwelcome if EITHER the in-app switch or the OS says so.
   *
   * The port is the settings toggle, and `storage.ts` documents that flag as "honor
   * prefers-reduced-motion even when the OS setting is off" — an ADDITIVE override, not a
   * mirror. Reading only the toggle therefore ignored every player who had asked their
   * operating system for stillness and never opened our Settings panel. The stylesheet had
   * always honoured the media query; this was the one animation written in JS.
   */
  const stillWanted = (): boolean =>
    opts.reducedMotion?.() === true ||
    (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);

  const countUp = (el: HTMLElement, text: string): void => {
    const n = Number(text.replace(/[^0-9]/g, ""));
    // Only numbers count up, and only when motion is welcome. "10 s" animates to nothing.
    if (
      !Number.isFinite(n) ||
      n <= 0 ||
      stillWanted() ||
      typeof requestAnimationFrame !== "function"
    ) {
      el.textContent = text;
      return;
    }
    const start = performance.now();
    const DUR = 900;
    const tick = (t: number): void => {
      const k = Math.min(1, (t - start) / DUR);
      const eased = 1 - (1 - k) * (1 - k) * (1 - k);
      el.textContent = k >= 1 ? text : group(Math.round(n * eased));
      if (k < 1) requestAnimationFrame(tick);
    };
    el.textContent = group(0);
    requestAnimationFrame(tick);
  };

  /**
   * D237: the handoff asks for the count-up ON SCROLL INTO VIEW, and that is not decoration.
   *
   * The band is below the fold, and the numbers arrive when the boot fetch lands — so the
   * animation was running to completion while nobody was looking at it, and by the time a
   * player scrolled down the figures were already sitting still. Deferring it to the moment
   * the band is actually on screen is the whole point of the requirement.
   *
   * `IntersectionObserver` is absent in jsdom and on old Safari; without it the number simply
   * counts up immediately, which is the pre-D237 behaviour and never a blank cell.
   */
  const run = (el: HTMLElement, text: string): void => {
    if (typeof IntersectionObserver !== "function" || stillWanted()) {
      countUp(el, text);
      return;
    }
    el.textContent = group(0);
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect(); // once: a stat that re-counted on every scroll past would be a tic
        countUp(el, text);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
  };

  return {
    root,
    update(activity: ActivityStat | null): void {
      const cells = proofStats(activity);
      row.replaceChildren();
      // One real cell plus the claim is not a proof bar; it is a claim with decoration.
      // Below that the whole band stands down rather than advertising its own emptiness.
      root.hidden = cells.length < 2;
      for (const c of cells) {
        const cell = doc.createElement("div");
        cell.className = "proof-cell";
        cell.dataset["proofCell"] = c.label;
        const v = doc.createElement("div");
        v.className = "proof-value";
        const l = doc.createElement("div");
        l.className = "proof-label";
        l.textContent = c.label;
        cell.append(v, l);
        row.append(cell);
        run(v, c.value);
      }
    },
  };
}

export interface TestimonialsOpts {
  doc: Document;
  /** Real, consented quotes. Empty (the state today) ⇒ no cards render. */
  quotes?: readonly Testimonial[];
  /** Real post-game ratings. Null (the state today) ⇒ no aggregate line. */
  aggregate?: RatingAggregate | null;
  /**
   * D238: what CLOSES the page.
   *
   * This slot used to hold the band's own sign-up CTA. That offer moved into the hero — the
   * page was making it twice, in two voices — and the slot now takes whatever the caller
   * wants the last word to be. Omitted, the band simply has no closing element, and with no
   * quotes and no ratings either it renders nothing at all and hides itself, which is the
   * honest empty state rather than a heading over an argument it cannot make.
   */
  closing?: HTMLElement;
}

/**
 * The "from the tables" band.
 *
 * With no quotes and no ratings it collapses to its closing call to action — which is the
 * honest version of this section, not a degraded one: the CTA is a true statement about a
 * free account, and the heading "Settlers can't stop talking" over zero quotes would be an
 * advertisement for evidence that does not exist.
 */
export interface TestimonialBand {
  readonly root: HTMLElement;
  /** Re-read the auth state. D222: the CTA is an offer, and an offer can go stale. */
  refresh(): void;
}

export function createTestimonials(opts: TestimonialsOpts): TestimonialBand {
  const d = opts.doc;
  const root = d.createElement("section");
  root.className = "tmn";
  root.dataset["testimonials"] = "1";

  const quotes = opts.quotes ?? [];
  const agg = opts.aggregate ?? null;

  const build = (): void => {
    root.replaceChildren();
    if (quotes.length > 0) {
      const kicker = d.createElement("div");
      kicker.className = "tmn-kicker";
      kicker.textContent = "FROM THE TABLES";
      const head = d.createElement("h2");
      head.className = "tmn-head";
      head.textContent = "Settlers can’t stop talking";
      root.append(kicker, head);

      const cards = d.createElement("div");
      cards.className = "tmn-cards";
      for (const q of quotes) {
        const card = d.createElement("figure");
        card.className = "tmn-card";
        card.dataset["testimonial"] = "1";
        const stars = d.createElement("div");
        stars.className = "tmn-stars";
        for (let i = 0; i < q.stars; i++) stars.append(star(d, 16));
        const text = d.createElement("blockquote");
        text.className = "tmn-text";
        text.textContent = q.text;
        const foot = d.createElement("figcaption");
        foot.className = "tmn-foot";
        const av = d.createElement("span");
        av.className = "tmn-avatar";
        av.style.background = q.color;
        av.textContent = q.initial;
        const name = d.createElement("span");
        name.className = "tmn-name";
        name.textContent = q.name;
        const meta = d.createElement("span");
        meta.className = "tmn-meta";
        meta.textContent = q.meta;
        foot.append(av, name, meta);
        card.append(stars, text, foot);
        cards.append(card);
      }
      root.append(cards);
    }

    if (showsAggregate(agg) && agg) {
      const line = d.createElement("div");
      line.className = "tmn-agg";
      line.dataset["ratingAggregate"] = "1";
      const s = d.createElement("span");
      s.className = "tmn-stars";
      for (let i = 0; i < 5; i++) s.append(star(d, 20));
      const avg = d.createElement("span");
      avg.className = "tmn-avg";
      avg.textContent = agg.average.toFixed(1);
      const note = d.createElement("span");
      note.className = "tmn-agg-note";
      note.textContent = `average from ${group(agg.count)} post-game ratings`;
      line.append(s, avg, note);
      root.append(line);
    }

    // The closing element is the one thing here that needs no evidence — so it is also the
    // one thing that survives when there is none.
    if (opts.closing) {
      const wrap = d.createElement("div");
      wrap.className = "tmn-cta-wrap";
      wrap.append(opts.closing);
      root.append(wrap);
    }

    root.hidden = root.childElementCount === 0;
  };

  build();
  // D222 read `signedIn` ONCE at mount and left the page's largest gold button still selling
  // a free account after sign-in; the rebuild was the fix. D238 moved that offer into the
  // hero, so nothing in this band depends on auth any more — `refresh` stays because quotes
  // and ratings will one day arrive after mount, which is the same shape of problem.
  return { root, refresh: build };
}
